import { Component } from '@angular/core';
import { Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmComponent } from '../confirm/confirm.component';
import { ConfigService } from '../../services/config.service';
import { AlertsService } from '../../services/alerts.service';
import { UserService } from '../../services/user.service';
import { ProductService } from '../../services/product.service';
import { formatString } from '../../utils/string-utils';

/**
 * Component providing product version management.
 */
@Component({
	selector: 'revision-history',
	templateUrl: './revision-history.component.html',
	styleUrl: './revision-history.component.css'
})
export class RevisionHistoryComponent implements OnInit {
	@Input() product: any;

	LOCALIZATION: any;
	userPermissions: { [key: string]: any } = {};
	username: string | null = "";

	revisions: any[] = [];
	currentRevision: any = {};
	revertTarget: number = -1;
	lockObj: any = {};
	file?: File;
	currentIndex: number = 0;
	inprogress: boolean = false;

	notes = {
		limit: 144
	};

	downloadLink: string | null = null;

	constructor(
		private configService: ConfigService,
		private alertsService: AlertsService,
		private userService: UserService,
		private productService: ProductService,
		private ngbModal: NgbModal,
		private ngbmodalService: NgbActiveModal
	) { }

	ngOnInit(): void {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.userPermissions = this.userService.getUserPermissionsMap();
		this.username = this.userService.user.username;
		this.downloadLink = formatString(this.configService.getConfig().document.documentDownloadURL, this.product.id);

		this.isLocked();
	}

	isLocked(): void {
		this.productService.isLocked(this.product.id).then(
			(lock: any) => {
				this.lockObj = lock;
				if (this.lockObj) {
					if (!this.lockObj.lockType || (this.username == this.lockObj.lockedBy)) {
						this.loadHistory();
					}
				} else {
					this.loadHistory();
				}
			},
			(err: any) => {
				console.error("Failed to lock product", err);
				let msg = err || this.LOCALIZATION.lockFailed;
				this.alertsService.error({
					message: msg
				});
			}
		);
	}

	/**
	 * Loads the product version history.
	 */
	loadHistory(): void {
		if (this.revisions && this.revisions.length > 0) {
			this.productService.updateRevisions(this.revisions);
		}

		let pagination = {
			page: 0,
			size: 20,
			sort: "versionNumber,desc"
		};

		this.productService.getRevisions(this.product.id, pagination).then(
			(response: any) => {
				this.revisions = response;
				if (this.revisions.length > 0) {
					this.selectRevision(0);
				}
			},
			(err: any) => {
				console.error("Failed to get product revision history", err);
				this.alertsService.error({
					message: "Failed to retrieve revision history for this document."
				});
				this.revisions = [];
			}
		);
	}

	/**
	 * Updates the product version history.
	 */
	updateHistory(): void {
		this.inprogress = true;

		this.productService.updateRevisions(this.revisions).then(
			(response: any) => {
				this.inprogress = false;
				this.alertsService.success({
					message: "Updated notes to the latest changes."
				});
			},
			(err: any) => {
				console.error("Failed to update product revision history", err);
				this.inprogress = false;
				this.alertsService.error({
					message: "Error updating notes."
				});
			}
		);
	}

	/**
	 * Checks out the product.
	 */
	checkOut(): void {
		this.productService.checkoutProduct(this.product.id).then(
			(lock: any) => {
				this.lockObj = lock;
			},
			(err: any) => {
				console.error("Failed to check out product", err);
				let msg = err || this.LOCALIZATION.lockFailed;
				this.alertsService.error({
					message: msg
				});
			}
		);
	}

	/**
	 * Removes the product checkout.
	 */
	undoCheckOut(): void {
		this.productService.undoCheckout(this.product.id).then(
			(lock: any) => {
				this.lockObj = {};
			},
			(err: any) => {
				console.error("Failed to undo product check out", err);
				let msg = err || this.LOCALIZATION.lockFailed;
				this.alertsService.error({
					message: msg
				});
			}
		);
	}

	/**
	 * Checks in the product as a new version.
	 */
	checkIn(): void {
		if (this.file) {
			const modalRef = this.ngbModal.open(ConfirmComponent, {
				size: 'sm'
			});

			modalRef.componentInstance.modal = {
				callerName: 'checkIn',
				title: this.LOCALIZATION.replaceHeader,
				message: this.LOCALIZATION.replaceMessage,
				type: 'warning'
			};

			modalRef.result.then(
				(result) => {
					if (result.result?.answer === 'ok') {
						this.productService.checkinProduct(this.product.id, this.file).then(
							(response: any) => {
								if (response) {
									this.alertsService.success({
										message: this.LOCALIZATION.replaceSuccess
									});
								} else {
									this.alertsService.error({
										message: this.LOCALIZATION.replaceError
									});
								}
							},
							(err: any) => {
								console.error("Failed to check in product", err);
								this.alertsService.error({
									message: this.LOCALIZATION.replaceError
								});
							}
						);
					}
				},
				(reason) => {
					console.log('Modal dismissed');
				}
			);
		} else {
			this.alertsService.error({
				title: "No Files to Upload",
				message: "Please select a file to upload."
			});
		}
	}

	addFile(file: File): void {
		this.file = file;
	}

	onFilesSelected(files: File[]) {
		files.forEach(file => {
			console.log('Added file:', file);
			this.addFile(file);
		});
	}

	selectRevision(index: number): void {
		this.currentIndex = index;
		this.currentRevision = this.revisions[index];
	}

	selectRevertTarget(index: number): void {
		this.revertTarget = index === this.revertTarget ? -1 : index;
	}

	/**
	 * Reverts the product to the chosen version.
	 */
	revert(): void {
		const modalRef = this.ngbModal.open(ConfirmComponent, {
			size: 'sm'
		});

		modalRef.componentInstance.modal = {
			callerName: 'revert',
			title: this.LOCALIZATION.revertHeader,
			message: this.LOCALIZATION.revertMessage,
			type: 'warning'
		};

		modalRef.result.then(
			(result) => {
				if (result.result?.answer === 'ok') {
					this.productService.revertRevision(this.product.id, this.currentRevision).then(
						(response: any) => {
							this.alertsService.success({
								message: this.LOCALIZATION.revertSuccess
							});
							this.loadHistory();
						},
						(err: any) => {
							console.error("Failed to revert product", err);
							this.alertsService.error({
								message: this.LOCALIZATION.revertError
							});
						});
				}
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Downloads the product.
	 */
	download(): void {
		setTimeout(() => {
			this.inprogress = true;
		}, 500);

		if (!this.inprogress) {
			this.alertsService.info({
				title: this.LOCALIZATION.downloadStarted,
				message: "Please wait for the download process to finish. PDF formats take longer.",
				delay: 10000
			});
		}

		setTimeout(() => {
			this.inprogress = false;
		}, 1000);

	}

	/**
	 * Closes the modal.
	 */
	close(): void {
		if (this.revisions.length > 0) {
			this.productService.updateRevisions(this.revisions);
		}

		this.ngbmodalService.dismiss();
	}
}
